import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import Loader from '../../Components/Loader';
import CustomButton from "../../Components/CustomButton";
import CustomModal from "../../Components/CustomModal";

import "./style.css";

import { useGetAdPricingQuery, useUpdateAdPricingMutation } from "../../Redux/Apis/AdPricing";

const AdPricing = () => {
  const { data, isLoading, refetch } = useGetAdPricingQuery();
  const [updatePricing, { isLoading: isUpdating }] = useUpdateAdPricingMutation();

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    adSizePrices: {
      standard: 49.99,
      bronze: 99.99,
      silver: 149.99,
      gold: 249.99
    },
    revenueMultipliers: {
      'under50k': 1.0,
      '50k-100k': 1.25,
      '100k-500k': 1.5,
      'over500k': 2.0
    },
    yearlyDiscount: 0.20
  });

  // Load data into form when available
  useEffect(() => {
    if (data?.data) {
      setFormData({
        adSizePrices: {
          standard: data.data.adSizePrices?.standard || 49.99,
          bronze: data.data.adSizePrices?.bronze || 99.99,
          silver: data.data.adSizePrices?.silver || 149.99,
          gold: data.data.adSizePrices?.gold || 249.99
        },
        revenueMultipliers: {
          'under50k': data.data.revenueMultipliers?.['under50k'] || 1.0,
          '50k-100k': data.data.revenueMultipliers?.['50k-100k'] || 1.25,
          '100k-500k': data.data.revenueMultipliers?.['100k-500k'] || 1.5,
          'over500k': data.data.revenueMultipliers?.['over500k'] || 2.0
        },
        yearlyDiscount: data.data.yearlyDiscount || 0.20
      });
    }
  }, [data]);

  const handlePriceChange = (size, value) => {
    setFormData(prev => ({
      ...prev,
      adSizePrices: {
        ...prev.adSizePrices,
        [size]: parseFloat(value) || 0
      }
    }));
  };

  const handleMultiplierChange = (tier, value) => {
    setFormData(prev => ({
      ...prev,
      revenueMultipliers: {
        ...prev.revenueMultipliers,
        [tier]: parseFloat(value) || 0
      }
    }));
  };

  const handleYearlyDiscountChange = (value) => {
    setFormData(prev => ({
      ...prev,
      yearlyDiscount: parseFloat(value) / 100 || 0 // Convert from percentage
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await updatePricing(formData).unwrap();
      setShowSuccessModal(true);
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update pricing');
    }
  };

  useEffect(() => {
    document.title = 'Landing List | Ad Pricing Management';
  }, []);

  if (isLoading) {
    return (
      <DashboardLayout>
        <Loader />
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mainTitle">Ad Pricing Management</h2>
                </div>

                <p className="text-muted mb-4">
                  Configure ad pricing for vendors. Changes will apply to new ad purchases only.
                  Existing subscriptions will retain their original pricing.
                </p>

                <form onSubmit={handleSubmit}>
                  {/* Ad Size Pricing Section */}
                  <div className="pricing-section mb-5">
                    <h4 className="section-title">
                      <span className="badge bg-primary me-2">1</span>
                      Base Prices (Monthly)
                    </h4>
                    <p className="section-description">Set the base monthly price for each ad size tier.</p>

                    <div className="row">
                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="pricing-card standard">
                          <div className="pricing-card-header">
                            <h5>Standard</h5>
                            <span className="badge bg-secondary">1/8 Page</span>
                          </div>
                          <div className="pricing-card-body">
                            <div className="input-group">
                              <span className="input-group-text">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control form-control-lg"
                                value={formData.adSizePrices.standard}
                                onChange={(e) => handlePriceChange('standard', e.target.value)}
                              />
                              <span className="input-group-text">/mo</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="pricing-card bronze">
                          <div className="pricing-card-header">
                            <h5>Bronze</h5>
                            <span className="badge" style={{ backgroundColor: '#CD7F32' }}>1/4 Page</span>
                          </div>
                          <div className="pricing-card-body">
                            <div className="input-group">
                              <span className="input-group-text">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control form-control-lg"
                                value={formData.adSizePrices.bronze}
                                onChange={(e) => handlePriceChange('bronze', e.target.value)}
                              />
                              <span className="input-group-text">/mo</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="pricing-card silver">
                          <div className="pricing-card-header">
                            <h5>Silver</h5>
                            <span className="badge" style={{ backgroundColor: '#C0C0C0', color: '#333' }}>1/3 Page</span>
                          </div>
                          <div className="pricing-card-body">
                            <div className="input-group">
                              <span className="input-group-text">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control form-control-lg"
                                value={formData.adSizePrices.silver}
                                onChange={(e) => handlePriceChange('silver', e.target.value)}
                              />
                              <span className="input-group-text">/mo</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="pricing-card gold">
                          <div className="pricing-card-header">
                            <h5>Gold</h5>
                            <span className="badge" style={{ backgroundColor: '#FFD700', color: '#333' }}>1/2 Page</span>
                          </div>
                          <div className="pricing-card-body">
                            <div className="input-group">
                              <span className="input-group-text">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control form-control-lg"
                                value={formData.adSizePrices.gold}
                                onChange={(e) => handlePriceChange('gold', e.target.value)}
                              />
                              <span className="input-group-text">/mo</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Multipliers Section */}
                  <div className="pricing-section mb-5">
                    <h4 className="section-title">
                      <span className="badge bg-primary me-2">2</span>
                      Revenue-Based Multipliers
                    </h4>
                    <p className="section-description">
                      Multiply base prices by these factors based on the vendor's yearly revenue.
                      A multiplier of 1.0 means no change; 1.5 means 50% more.
                    </p>

                    <div className="row">
                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="multiplier-card">
                          <label className="form-label fw-bold">Under $50K</label>
                          <div className="input-group">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              value={formData.revenueMultipliers['under50k']}
                              onChange={(e) => handleMultiplierChange('under50k', e.target.value)}
                            />
                            <span className="input-group-text">×</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="multiplier-card">
                          <label className="form-label fw-bold">$50K - $100K</label>
                          <div className="input-group">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              value={formData.revenueMultipliers['50k-100k']}
                              onChange={(e) => handleMultiplierChange('50k-100k', e.target.value)}
                            />
                            <span className="input-group-text">×</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="multiplier-card">
                          <label className="form-label fw-bold">$100K - $500K</label>
                          <div className="input-group">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              value={formData.revenueMultipliers['100k-500k']}
                              onChange={(e) => handleMultiplierChange('100k-500k', e.target.value)}
                            />
                            <span className="input-group-text">×</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-3 mb-3">
                        <div className="multiplier-card">
                          <label className="form-label fw-bold">Over $500K</label>
                          <div className="input-group">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control"
                              value={formData.revenueMultipliers['over500k']}
                              onChange={(e) => handleMultiplierChange('over500k', e.target.value)}
                            />
                            <span className="input-group-text">×</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Yearly Discount Section */}
                  <div className="pricing-section mb-5">
                    <h4 className="section-title">
                      <span className="badge bg-primary me-2">3</span>
                      Yearly Billing Discount
                    </h4>
                    <p className="section-description">
                      Discount applied when vendors choose yearly billing instead of monthly.
                    </p>

                    <div className="row">
                      <div className="col-md-4">
                        <div className="discount-card">
                          <label className="form-label fw-bold">Yearly Discount</label>
                          <div className="input-group">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="100"
                              className="form-control form-control-lg"
                              value={Math.round(formData.yearlyDiscount * 100)}
                              onChange={(e) => handleYearlyDiscountChange(e.target.value)}
                            />
                            <span className="input-group-text">% off</span>
                          </div>
                          <small className="text-muted mt-2 d-block">
                            E.g., 20% means yearly costs 80% of (monthly × 12)
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Preview */}
                  <div className="pricing-section mb-5">
                    <h4 className="section-title">
                      <span className="badge bg-info me-2">Preview</span>
                      Price Examples
                    </h4>
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead className="table-dark">
                          <tr>
                            <th>Ad Size</th>
                            <th>Revenue Tier</th>
                            <th>Monthly</th>
                            <th>Yearly (with discount)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {['standard', 'bronze', 'silver', 'gold'].map(size => (
                            <tr key={size}>
                              <td className="fw-bold text-capitalize">{size}</td>
                              <td>Under $50K</td>
                              <td>
                                ${(formData.adSizePrices[size] * formData.revenueMultipliers['under50k']).toFixed(2)}/mo
                              </td>
                              <td>
                                ${(formData.adSizePrices[size] * formData.revenueMultipliers['under50k'] * 12 * (1 - formData.yearlyDiscount)).toFixed(2)}/yr
                              </td>
                            </tr>
                          ))}
                          <tr className="table-warning">
                            <td className="fw-bold">Gold</td>
                            <td>Over $500K</td>
                            <td>
                              ${(formData.adSizePrices.gold * formData.revenueMultipliers['over500k']).toFixed(2)}/mo
                            </td>
                            <td>
                              ${(formData.adSizePrices.gold * formData.revenueMultipliers['over500k'] * 12 * (1 - formData.yearlyDiscount)).toFixed(2)}/yr
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="d-flex justify-content-end">
                    <CustomButton
                      type="submit"
                      variant="primaryButton"
                      text={isUpdating ? "Saving..." : "Save Pricing"}
                      disabled={isUpdating}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <CustomModal
          show={showSuccessModal}
          close={() => setShowSuccessModal(false)}
          success
          heading="Pricing Updated Successfully"
        />
      </DashboardLayout>
    </>
  );
};

export default AdPricing;

